import type { Picture } from "@/lib/images";

const MIME: Record<string, string> = {
  avif: "image/avif",
  webp: "image/webp",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
};

/**
 * Renders a build-time generated <picture>: AVIF first, WebP next, JPEG
 * fallback. Intrinsic width/height always ship so the browser reserves the
 * space before the bytes arrive (no layout shift).
 */
export function AppImage({
  picture,
  alt,
  className,
  sizes = "100vw",
  priority = false,
  eager = false,
  draggable,
}: {
  picture: Picture;
  alt: string;
  className?: string | undefined;
  sizes?: string | undefined;
  priority?: boolean | undefined;
  /** Load immediately without claiming high priority (below-fold but near). */
  eager?: boolean | undefined;
  /** Pass false to suppress the browser's native image-drag gesture. */
  draggable?: boolean | undefined;
}) {
  const entries = Object.entries(picture.sources).filter(
    ([format]) => format !== "jpg" && format !== "jpeg",
  );

  return (
    <picture>
      {entries.map(([format, srcSet]) => (
        <source
          key={format}
          type={MIME[format] ?? `image/${format}`}
          srcSet={srcSet}
          sizes={sizes}
        />
      ))}
      <img
        src={picture.img.src}
        srcSet={picture.sources["jpg"] ?? picture.sources["jpeg"]}
        sizes={sizes}
        alt={alt}
        width={picture.img.w}
        height={picture.img.h}
        className={className}
        loading={priority || eager ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        {...(draggable === undefined ? {} : { draggable })}
        {...(priority ? { fetchPriority: "high" as const } : {})}
      />
    </picture>
  );
}
